import React, { useState, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import Icon from "@mdi/react";
import useEscapeKey from "../hooks/useEscapeKey";
import {
    mdiEmail,
    mdiPhone,
    mdiAlert,
    mdiMapMarker,
    mdiContentSave,
    mdiCheck,
    mdiClose,
    mdiMessageText,
    mdiTrashCan,
    mdiPencil,
    mdiSend,
    mdiRefresh,
    mdiAccountPlus,
    mdiAccountSearchOutline,
    mdiCalendarCheck,
    mdiAirplaneTakeoff,
    mdiClockEditOutline,
    mdiCreditCardClockOutline,
    mdiCreditCardSyncOutline,
    mdiCashCheck,
    mdiAlertCircle,
    mdiCreditCardRefund,
} from "@mdi/js";
import { toast } from "react-toastify";
import "./Modal.css"; // Shared modal button styles
import "./BookingDetailModal.css";
import { bookingApi } from "../api/booking";
import webApiClient from "../api/client";
import { formatCurrency, formatCurrencyFromMinor } from "../utils/currency";
import VenuePriceChangeModal from "./VenuePriceChangeModal";
import BookingPaymentModal from "./BookingPaymentModal";
import ReturnActionPanel from "./ReturnActionPanel";
import TimeSelectDropdown from "./TimeSelectDropdown";
import ConfirmationModal from "./ConfirmationModal";
import avatarPlaceholder from "../assets/avatar_user_5.png";

const findTravelPeriodForDate = (isoDate, periods) => {
    if (!isoDate || !periods || !periods.length) return null;
    return (
        periods.find(period => {
            const start = period.startDate.substring(0, 10);
            const end = period.endDate.substring(0, 10);
            return isoDate >= start && isoDate <= end;
        }) || null
    );
};

/**
 * BookingDetailModal - Full booking details modal with edit functionality
 * Displays comprehensive booking information with edit mode, time controls, and overlap detection
 */
const BookingDetailModal = ({
    booking,
    allBookings = [], // All bookings for overlap detection
    availabilitySlots = [], // All availability slots for nudging
    instructorLocations = [], // Instructor's teaching locations
    travelPeriods = [], // For travel-context-aware location filtering
    currentUserId, // Current user's account ID for message display
    onClose,
    onConfirm,
    onReject,
    onMessage,
    onCancel, // New: cancel booking
    onUpdateTime, // New: update booking time
    onMessageSent, // New: callback when message is sent (for refreshing indicators)
    onMessagesRead, // New: callback when messages are marked as read (updates unreadCount in parent state)
    onReturnIssued, // New: callback after a refund/credit is issued (booking is now cancelled — refresh the list)
}) => {
    // Close on Escape — mirrors the backdrop-click behaviour. This modal is
    // mounted conditionally by the parent (no isOpen prop), so it is always
    // "open" while rendered; pass true so the shared stack pushes/pops on
    // mount/unmount and Escape only closes the topmost modal when stacked.
    useEscapeKey(true, onClose);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editedStartTime, setEditedStartTime] = useState("");
    const [editedEndTime, setEditedEndTime] = useState("");
    const [editedLocationId, setEditedLocationId] = useState("");
    const [overlaps, setOverlaps] = useState([]);
    const [slotConflict, setSlotConflict] = useState(false); // new time overlaps an availability slot
    const [showNudgeConfirm, setShowNudgeConfirm] = useState(false);
    const [previewPricing, setPreviewPricing] = useState(null);

    // Venue price-change modal state — intercepts handleSaveTime when a paid
    // booking has a venue change with non-zero fee delta. Three actions:
    // Consent-path venue adjustment: refund (Stripe partial + notify), charge via
    // in-app Elements (chat_message / studio_confirmed), or skip (no money move).
    const [showVenuePriceModal, setShowVenuePriceModal] = useState(false);
    const [venueChangeData, setVenueChangeData] = useState(null);
    const [venuePriceProcessing, setVenuePriceProcessing] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    // When true, the payment modal opens straight on the "Mark paid" method picker
    // (Mark paid is a distinct intent from Request Payment — in bookings the link is
    // usually already sent, so the instructor wants to record payment in one click).
    const [markPaidMode, setMarkPaidMode] = useState(false);

    // Return-action state (Entry Points 2 + 3)
    const [showReturnPanel, setShowReturnPanel] = useState(false);
    const [isIssuingReturn, setIsIssuingReturn] = useState(false);

    // Messaging state - always show messages
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [messageError, setMessageError] = useState(null);
    const messagesEndRef = useRef(null);

    // Character limit constant (matches mobile)
    const MESSAGE_CHAR_LIMIT = 500;

    // User linking state (for manual bookings)
    const [showLinkUser, setShowLinkUser] = useState(false);
    const [linkUserSearch, setLinkUserSearch] = useState("");
    const [linkUserResults, setLinkUserResults] = useState([]);
    const [isSearchingUsers, setIsSearchingUsers] = useState(false);
    const [isLinking, setIsLinking] = useState(false);

    // Currency matching and conversion helper (simplified - backend handles real conversion)
    const currenciesMatch = (curr1, curr2) => {
        return curr1?.toUpperCase() === curr2?.toUpperCase();
    };

    // Simple estimated conversion (backend uses real rates)
    const estimateConversion = (amount, fromCurrency, toCurrency) => {
        // Rough estimates for preview only (backend uses actual rates)
        const rates = {
            AUD: 1.0,
            USD: 0.65,
            GBP: 0.51,
            EUR: 0.59,
            NZD: 1.06,
        };

        const fromRate = rates[fromCurrency?.toUpperCase()] || 1.0;
        const toRate = rates[toCurrency?.toUpperCase()] || 1.0;

        return (amount / fromRate) * toRate;
    };

    // Calculate pricing preview based on current edit state
    const calculatePricingPreview = () => {
        if (!isEditMode || !booking.pricing) return null;

        // Get instructor currency and hourly rate from booking
        const instructorCurrency = booking.pricing.currency;

        // Calculate original duration from booking timeSlot
        const originalDurationMinutes =
            timeToMinutes(booking.timeSlot.endTime) - timeToMinutes(booking.timeSlot.startTime);
        const originalDurationHours = originalDurationMinutes / 60;

        // hourly rate is in integer cents (pricing.baseRate / artistFeeSubtotal are cents).
        const hourlyRateAmount = booking.pricing.baseRate || booking.pricing.artistFeeSubtotal / originalDurationHours;

        // Calculate duration in hours
        const durationMinutes = timeToMinutes(editedEndTime) - timeToMinutes(editedStartTime);
        const durationHours = durationMinutes / 60;

        // Calculate artist fee (hourly rate × duration) — cents.
        const artistFeeSubtotal = hourlyRateAmount * durationHours;

        // Find selected location's venue hire config. Venue-hire CONFIG is stored in
        // dollars; this preview works in integer cents, so convert config × 100.
        let venueHireSubtotal = 0;
        let venueHireOriginal = null;
        let isEstimated = false;

        if (editedLocationId) {
            const selectedLocation = instructorLocations.find(loc => loc.id === editedLocationId);

            if (selectedLocation?.venueHire?.enabled) {
                const venueHire = selectedLocation.venueHire;
                const venueHireCurrency = venueHire.currency;
                const venueHireAmountCents = (venueHire.amount || 0) * 100; // config dollars → cents
                const venueHireType = venueHire.type;

                const needsConversion = !currenciesMatch(instructorCurrency, venueHireCurrency);

                if (needsConversion) {
                    const convertedVenueHireAmount = estimateConversion(
                        venueHireAmountCents,
                        venueHireCurrency,
                        instructorCurrency
                    );

                    venueHireSubtotal =
                        venueHireType === "hourly"
                            ? convertedVenueHireAmount * durationHours
                            : convertedVenueHireAmount;

                    isEstimated = true;
                    venueHireOriginal = {
                        amount: venueHireAmountCents,
                        currency: venueHireCurrency,
                    };
                } else {
                    venueHireSubtotal =
                        venueHireType === "hourly" ? venueHireAmountCents * durationHours : venueHireAmountCents;
                }
            }
        }

        // Calculate platform fee (2.5% on artist fee ONLY)
        const PLATFORM_FEE_PERCENTAGE = 0.025;
        const platformFeeSubtotal = artistFeeSubtotal * PLATFORM_FEE_PERCENTAGE;

        // Calculate total
        const amount = artistFeeSubtotal + venueHireSubtotal + platformFeeSubtotal;

        return {
            currency: instructorCurrency,
            artistFeeSubtotal: Math.round(artistFeeSubtotal),
            venueHireSubtotal: Math.round(venueHireSubtotal),
            platformFeeSubtotal: Math.round(platformFeeSubtotal),
            amount: Math.round(amount),
            isEstimated,
            venueHireOriginal,
        };
    };

    useEffect(() => {
        if (booking) {
            setEditedStartTime(booking.timeSlot.startTime);
            setEditedEndTime(booking.timeSlot.endTime);

            // Match booking location to instructor's preferredLocations by description
            // booking.location.description should match one of the preferredLocations
            if (booking.location?.description && instructorLocations.length > 0) {
                const matchingLocation = instructorLocations.find(
                    loc => loc.description === booking.location.description
                );
                setEditedLocationId(matchingLocation?.id || "");
            } else {
                setEditedLocationId("");
            }

            // Load messages when modal opens (including re-opens of same booking)
            // Always refresh messages in view mode to ensure latest conversation
            if (!isEditMode) {
                loadMessages();
            }
        }
    }, [booking?.id, isEditMode, instructorLocations]); // Use booking.id to trigger refresh on any modal open

    // Scroll to bottom when messages change
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    // Recalculate pricing preview when edit mode values change.
    // Only show the preview once the admin actually changes the time or location —
    // on first open the edited values equal the booking's own, so there is nothing
    // new to quote (showing it then just surfaces a phantom "service fee").
    useEffect(() => {
        if (!isEditMode || !editedStartTime || !editedEndTime) {
            setPreviewPricing(null);
            return;
        }

        // Resolve the booking's original location id so we can detect a real change.
        const originalLocationId = booking.location?.description
            ? instructorLocations.find(loc => loc.description === booking.location.description)?.id || ""
            : "";

        // Only a DURATION change affects the price — the fee is hourlyRate × duration.
        // Quick-adjust shifts start + end together (same duration → same fee), so a
        // pure time shift must NOT trigger the preview; only extending/shortening the
        // lesson (or a location swap) does.
        const originalDuration = timeToMinutes(booking.timeSlot.endTime) - timeToMinutes(booking.timeSlot.startTime);
        const editedDuration = timeToMinutes(editedEndTime) - timeToMinutes(editedStartTime);
        const durationChanged = editedDuration !== originalDuration;
        const locationChanged = editedLocationId !== originalLocationId;

        if (durationChanged || locationChanged) {
            setPreviewPricing(calculatePricingPreview());
        } else {
            setPreviewPricing(null);
        }
    }, [isEditMode, editedStartTime, editedEndTime, editedLocationId]);

    if (!booking) return null;

    const isPending = booking.status === "pending";
    // Past-day, completed, and cancelled bookings stay openable (Request Payment,
    // Mark Paid, and Refund/Credit all live in this modal) but their details are
    // locked — no time/location edits. Same day boundary as the week view's grey
    // past styling (viewer's local today).
    const isPastBooking = (booking.bookingDate || "").substring(0, 10) < DateTime.local().toISODate();
    const canEditDetails = !isPastBooking && booking.status !== "completed" && booking.status !== "cancelled";
    // Credit-paid bookings are excluded: there is no card payment to refund, and the
    // only way to undo one is Cancel (which auto-returns the credit to the ledger).
    // Offering "Issue Refund" on them is meaningless. Bank-transfer-paid bookings DO
    // qualify — a return there grants account credit (ReturnActionPanel handles it).
    // Can still issue a (further) return: paid, not already FULLY returned, not credit-paid.
    // A 'partial' booking stays paid + active, so another partial refund can still be issued.
    const isPaidWithNoReturn =
        booking?.pricing?.paymentStatus === "paid" &&
        booking.returnStatus !== "refunded" &&
        booking.returnStatus !== "credited" &&
        booking?.effectivePricing?.paidBy !== "platform_credit";
    const isStudentCancelledWithNoReturn = booking?.cancellationType === "student-cancel" && isPaidWithNoReturn;
    const isGuest = booking?.studentId === "000000000000000000000000";

    // Server-computed display pricing (effectivePricing) — THE single source of
    // truth for what this booking costs / cost. Rendered verbatim; never compute
    // booking prices client-side (every second calculator eventually disagrees
    // with the canonical backend one).
    const ep = booking?.effectivePricing || null;
    const paidByLabel =
        ep?.paidBy === "platform_credit"
            ? "account credit"
            : ep?.paidBy === "bank_transfer"
              ? "bank transfer"
              : ep?.paidBy === "eftpos"
                ? "EFTPOS"
                : ep?.paidBy === "cash"
                  ? "cash"
                  : ep?.paidBy === "other"
                    ? "another method"
                    : null;

    // Travel-booking pill — mode-agnostic. Rendered directly under the time pickers
    // in edit mode (so it stays above the location context) and in its read-only
    // position in view mode. Extracted so both call sites share one definition.
    const renderTravelBanner = () => {
        const isoDate = booking.bookingDate?.substring(0, 10);
        const matchedPeriod = findTravelPeriodForDate(isoDate, travelPeriods);
        if (!matchedPeriod) return null;
        return (
            <div
                className="travel-period-banner"
                style={{
                    backgroundColor: `${matchedPeriod.color}1A`,
                    borderLeftColor: matchedPeriod.color,
                }}>
                <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Icon path={mdiAirplaneTakeoff} size={0.9} style={{ color: matchedPeriod.color, flexShrink: 0 }} />
                    <span className="travel-period-banner-text">Travel booking in {matchedPeriod.destination}</span>
                </span>
            </div>
        );
    };

    const handleReturnConfirm = async (type, amount, message) => {
        setIsIssuingReturn(true);
        try {
            await bookingApi.issueReturn(booking.id, { type, amount, message });
            // Externally-paid refunds are RECORDED (studio returns the money offline), not
            // reversed through Stripe — reflect that in the toast wording.
            const refundMsg = booking?.pricing?.paidOutsidePlatform ? "Refund recorded" : "Refund issued successfully";
            toast.success(type === "refund" ? refundMsg : "Credit granted to student");
            // Close the modal FIRST (instant), then refresh the list in the background — awaiting the
            // slow dashboard refetch before closing made the modal linger until the toast cleared.
            setShowReturnPanel(false);
            onClose();
            if (onReturnIssued) onReturnIssued();
        } catch (error) {
            toast.error("Could not issue return. Please try again.");
            setIsIssuingReturn(false);
        }
    };

    // Message Handlers
    const loadMessages = async () => {
        try {
            setIsLoadingMessages(true);
            const { messages: fetchedMessages } = await bookingApi.getMessages(booking.id);
            setMessages(fetchedMessages);

            const hasUnreadMessages = fetchedMessages.some(msg => !msg.read && msg.receiverId === currentUserId);

            if (hasUnreadMessages) {
                await bookingApi.markAllMessagesAsRead(booking.id);

                // Update the unread badge in the parent list immediately without a full data reload
                if (onMessagesRead) {
                    onMessagesRead(booking.id);
                }
            }
        } catch (error) {
            console.error("Error loading messages:", error);
        } finally {
            setIsLoadingMessages(false);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || isSendingMessage) return;

        try {
            setIsSendingMessage(true);
            setMessageError(null); // Clear any previous errors
            await bookingApi.sendMessage(booking.id, newMessage.trim());
            setNewMessage("");
            await loadMessages(); // Reload messages to show the new one

            // Notify parent component to refresh booking data (for message indicators)
            if (onMessageSent) {
                onMessageSent(booking.id);
            }
        } catch (error) {
            console.error("Error sending message:", error);
            setMessageError("Failed to send message. Please check your connection and try again.");
        } finally {
            setIsSendingMessage(false);
        }
    };

    const handleRefreshMessages = async () => {
        await loadMessages();
    };

    const formatMessageTime = timestamp => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return "Just now";
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    // User Linking Handlers (for manual bookings)
    const handleUserSearch = async searchQuery => {
        if (!searchQuery || searchQuery.length < 2) {
            setLinkUserResults([]);
            return;
        }

        try {
            setIsSearchingUsers(true);
            const response = await webApiClient.get(
                `/api/v2/instructor/search-students?query=${encodeURIComponent(searchQuery)}`
            );

            if (response.ok && response.data?.students) {
                setLinkUserResults(response.data.students);
            }
        } catch (error) {
            console.error("Error searching users:", error);
            setLinkUserResults([]);
        } finally {
            setIsSearchingUsers(false);
        }
    };

    const handleLinkUser = async userId => {
        try {
            setIsLinking(true);
            await bookingApi.linkUserToBooking(booking.id, userId);

            // Update booking data in parent component
            if (onMessageSent) {
                onMessageSent(booking.id);
            }

            alert("User linked to booking successfully! The booking will now show the linked user's details.");

            // Close modal to allow parent to refresh with updated booking data
            onClose();
        } catch (error) {
            console.error("Error linking user:", error);
            alert(error.message || "Failed to link user to booking");
        } finally {
            setIsLinking(false);
        }
    };

    // Timeline Math Functions
    const timeToMinutes = timeString => {
        const [hours, minutes] = timeString.split(":").map(Number);
        return hours * 60 + minutes;
    };

    const addMinutes = (timeString, minutesToAdd) => {
        const totalMinutes = timeToMinutes(timeString) + minutesToAdd;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    };
    const minutesToTime = mins => {
        const clamped = Math.max(0, Math.min(mins, 24 * 60));
        const h = Math.floor(clamped / 60),
            m = clamped % 60;
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    };

    // Does the proposed new time overlap a same-day AVAILABILITY SLOT (other than the
    // nudged booking's own slot)? This makes the nudge fire on slots-OR-bookings.
    const detectSlotOverlap = (newStartTime, newEndTime) => {
        const dayStr = booking.bookingDate.split("T")[0];
        const ns = timeToMinutes(newStartTime),
            ne = timeToMinutes(newEndTime);
        const origStart = timeToMinutes(booking.timeSlot.startTime);
        return (availabilitySlots || []).some(s => {
            if (s.date !== dayStr || !s.startTime || !s.endTime) return false;
            if (timeToMinutes(s.startTime) === origStart) return false; // the nudged booking's own slot
            return ns < timeToMinutes(s.endTime) && ne > timeToMinutes(s.startTime);
        });
    };

    // Compute the explicit shift plan (mode 'gap' = up-to-gap, 'whole' = shift everything).
    // The nudged booking's OWN slot (the anchor) always rides with the booking by `delta`, in
    // both modes, so no leftover slot is stranded at the original time. The FOLLOWING occupied
    // blocks (availability slots each carrying the booking that sits in them, plus any orphan
    // bookings) then cascade from the nudged booking's ORIGINAL end.
    // Returns { bookingShifts, slotShifts } for the backend to apply.
    const buildNudgePlan = mode => {
        const dayStr = booking.bookingDate.split("T")[0];
        const origStart = timeToMinutes(booking.timeSlot.startTime);
        const origEnd = timeToMinutes(booking.timeSlot.endTime);
        const newStart = timeToMinutes(editedStartTime);
        const newEnd = timeToMinutes(editedEndTime);
        const delta = newStart - origStart;
        const cutoff = origEnd;

        const dayBookings = (allBookings || []).filter(
            b =>
                b.id !== booking.id &&
                b.bookingDate.split("T")[0] === dayStr &&
                ["pending", "confirmed"].includes(b.status)
        );
        const daySlots = (availabilitySlots || []).filter(s => s.date === dayStr && s.startTime && s.endTime);

        // Anchor slot = the availability slot the nudged booking sits in. It moves WITH the booking
        // (by delta), so the old position isn't left behind. Skip when the start didn't move.
        let anchorShift = null;
        if (delta !== 0) {
            const anchor = daySlots.find(
                s => timeToMinutes(s.startTime) <= origStart && timeToMinutes(s.endTime) > origStart
            );
            if (anchor) {
                const aStart = timeToMinutes(anchor.startTime);
                const aEnd = timeToMinutes(anchor.endTime);
                anchorShift = {
                    oldStartTime: anchor.startTime,
                    newStartTime: minutesToTime(aStart + delta),
                    newEndTime: minutesToTime(aEnd + delta),
                    slotDuration: anchor.slotDuration || aEnd - aStart,
                };
            }
        }

        const slotBookingId = s => {
            const m = dayBookings.find(
                b =>
                    timeToMinutes(b.timeSlot.startTime) < timeToMinutes(s.endTime) &&
                    timeToMinutes(b.timeSlot.endTime) > timeToMinutes(s.startTime)
            );
            return m ? m.id : null;
        };

        // One block per interval (a slot carries its booking); plus orphan bookings with no slot.
        const items = [];
        daySlots.forEach(s => {
            if (timeToMinutes(s.startTime) >= cutoff) {
                items.push({
                    fromSlot: true,
                    startTime: s.startTime,
                    endTime: s.endTime,
                    slotDuration: s.slotDuration || timeToMinutes(s.endTime) - timeToMinutes(s.startTime),
                    bookingId: slotBookingId(s),
                });
            }
        });
        dayBookings.forEach(b => {
            const bStart = timeToMinutes(b.timeSlot.startTime);
            if (bStart < cutoff) return;
            const covered = daySlots.some(
                s =>
                    timeToMinutes(s.startTime) >= cutoff &&
                    timeToMinutes(s.startTime) < timeToMinutes(b.timeSlot.endTime) &&
                    timeToMinutes(s.endTime) > bStart
            );
            if (!covered) {
                items.push({
                    fromSlot: false,
                    startTime: b.timeSlot.startTime,
                    endTime: b.timeSlot.endTime,
                    slotDuration: timeToMinutes(b.timeSlot.endTime) - bStart,
                    bookingId: b.id,
                });
            }
        });
        items.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

        const moved = [];
        if (mode === "whole") {
            items.forEach(it =>
                moved.push({ item: it, ns: timeToMinutes(it.startTime) + delta, ne: timeToMinutes(it.endTime) + delta })
            );
        } else {
            let cursor = newEnd;
            for (const it of items) {
                const s = timeToMinutes(it.startTime),
                    e = timeToMinutes(it.endTime);
                if (s >= cursor) break; // a gap absorbs the shift — stop cascading
                moved.push({ item: it, ns: cursor, ne: cursor + (e - s) });
                cursor = cursor + (e - s);
            }
        }

        const bookingShifts = moved
            .filter(m => m.item.bookingId)
            .map(m => ({ bookingId: m.item.bookingId, startTime: minutesToTime(m.ns), endTime: minutesToTime(m.ne) }));
        const slotShifts = moved
            .filter(m => m.item.fromSlot)
            .map(m => ({
                oldStartTime: m.item.startTime,
                newStartTime: minutesToTime(m.ns),
                newEndTime: minutesToTime(m.ne),
                slotDuration: m.item.slotDuration,
            }));

        // Anchor slot rides with the booking (prepended so it's applied alongside the cascade).
        if (anchorShift) slotShifts.unshift(anchorShift);

        return { bookingShifts, slotShifts };
    };

    // Smart Stepwise Overlap Detection
    const detectStepwiseOverlaps = (newStartTime, newEndTime) => {
        // Get all bookings on the same day (excluding current booking)
        // Only check active bookings (pending, confirmed, paid) - ignore cancelled/completed
        const dayBookings = allBookings
            .filter(
                b =>
                    b.id !== booking.id &&
                    b.bookingDate.split("T")[0] === booking.bookingDate.split("T")[0] &&
                    ["pending", "confirmed"].includes(b.status)
            )
            .sort((a, b) => timeToMinutes(a.timeSlot.startTime) - timeToMinutes(b.timeSlot.startTime));

        const newStartMinutes = timeToMinutes(newStartTime);
        const newEndMinutes = timeToMinutes(newEndTime);
        const overlappingBookings = [];

        // Find which bookings would be affected
        for (const b of dayBookings) {
            const bStart = timeToMinutes(b.timeSlot.startTime);
            const bEnd = timeToMinutes(b.timeSlot.endTime);

            // Check if this booking overlaps with our new time
            // Overlap occurs when: new booking starts before existing ends AND new booking ends after existing starts
            if (newStartMinutes < bEnd && newEndMinutes > bStart) {
                overlappingBookings.push(b);
            } else if (bStart >= newEndMinutes) {
                // No more overlaps - stop checking (stepwise optimization)
                // Since bookings are sorted by start time, if this booking starts after our new booking ends,
                // all remaining bookings will also start after our new booking ends
                break;
            }
        }

        return overlappingBookings;
    };

    // Quick Time Adjustment
    const handleQuickAdjust = minutes => {
        const newStartTime = addMinutes(editedStartTime, minutes);
        const duration = timeToMinutes(editedEndTime) - timeToMinutes(editedStartTime);
        const newEndTime = addMinutes(newStartTime, duration);

        setEditedStartTime(newStartTime);
        setEditedEndTime(newEndTime);

        // Check for overlaps (bookings + availability slots)
        setOverlaps(detectStepwiseOverlaps(newStartTime, newEndTime));
        setSlotConflict(detectSlotOverlap(newStartTime, newEndTime));
    };

    // Handle Time Picker Change
    const handleTimeChange = (field, value) => {
        if (field === "start") {
            setEditedStartTime(value);
            // Auto-set end time to start + 1 hour (standard class duration)
            // User can still manually override end time afterward
            const newEndTime = addMinutes(value, 60);
            setEditedEndTime(newEndTime);

            setOverlaps(detectStepwiseOverlaps(value, newEndTime));
            setSlotConflict(detectSlotOverlap(value, newEndTime));
        } else {
            setEditedEndTime(value);

            setOverlaps(detectStepwiseOverlaps(editedStartTime, value));
            setSlotConflict(detectSlotOverlap(editedStartTime, value));
        }
    };

    // Save Time Changes
    const handleSaveTime = async () => {
        if (overlaps.length > 0 || slotConflict) {
            setShowNudgeConfirm(true);
        } else {
            // Prepare update data - only include changed fields
            const updateData = {};

            // Check if time changed
            const timeChanged =
                editedStartTime !== booking.timeSlot.startTime || editedEndTime !== booking.timeSlot.endTime;

            if (timeChanged) {
                updateData.startTime = editedStartTime;
                updateData.endTime = editedEndTime;
            }

            // Check if location changed (compare by description)
            if (editedLocationId) {
                const selectedLocation = instructorLocations.find(loc => loc.id === editedLocationId);
                if (selectedLocation && selectedLocation.description !== booking.location?.description) {
                    updateData.location = {
                        type: "instructor_studio",
                        locationId: selectedLocation.id,
                        snapshot: {
                            description: selectedLocation.description,
                            address: selectedLocation.address || "",
                            latitude: selectedLocation.latitude,
                            longitude: selectedLocation.longitude,
                        },
                    };
                }
            } else if (booking.location?.description && !booking.location.description.includes("TBA")) {
                // User explicitly switched from a named location to TBA — send explicit TBA payload
                updateData.location = {
                    type: "instructor_studio",
                    locationId: null,
                    snapshot: {
                        description: "Location TBA",
                        address: "",
                        latitude: null,
                        longitude: null,
                    },
                };
            }

            // Paid-booking + venue-change intercept:
            // The standard PATCH /time endpoint deliberately skips pricing recalc on
            // paid bookings to preserve the originally charged amount. Instead, route
            // venue changes through the dedicated payment-adjustment endpoint so the
            // instructor explicitly chooses refund / charge / skip.
            const isPaidBooking = booking.pricing?.paymentStatus === "paid";
            const venueChanged = !!updateData.location;
            const oldVenueFee = booking.pricing?.venueHireSubtotal || 0;
            const newVenueFee = previewPricing?.venueHireSubtotal ?? oldVenueFee;
            const feeDelta = parseFloat((newVenueFee - oldVenueFee).toFixed(2));

            if (isPaidBooking && venueChanged && feeDelta !== 0 && updateData.location?.locationId) {
                const newLocationId = updateData.location.locationId;
                const newLoc = instructorLocations.find(l => l.id === newLocationId);
                const newVenueName = newLoc?.description || "New venue";
                const studentFirst = (booking.studentName || "there").split(" ")[0];
                // Editable chat note for the chat_message path. The secure pay link is sent by
                // email (the chat can't render a tappable link), so the note points there.
                const defaultDraft =
                    feeDelta > 0
                        ? `Hi ${studentFirst}, I've had to move your lesson to ${newVenueName}, which has a slightly higher hire fee, so there's an extra ${formatCurrencyFromMinor(Math.abs(feeDelta), booking.pricing?.currency)}. Hope that's okay! You can pay the difference securely via the link I've emailed you. Any questions, just reply here.`
                        : "";
                setVenueChangeData({
                    oldVenueName: booking.location?.description || "Previous venue",
                    newVenueName,
                    oldFee: oldVenueFee,
                    newFee: newVenueFee,
                    delta: feeDelta,
                    currency: booking.pricing?.currency,
                    newLocationId,
                    defaultDraft,
                });
                setShowVenuePriceModal(true);
                return; // halt the standard save — wait for instructor's modal action
            }

            // No conflicts - save directly and wait for completion (refreshes calendar)
            await onUpdateTime(booking.id, updateData);
            setIsEditMode(false);
            onClose();
        }
    };

    // Single venue-adjustment handler — the modal builds the payload
    // ({ action, path, ...extras }); we merge newLocationId and toast on the result.
    const handleVenueAdjustment = async payload => {
        if (!venueChangeData) return;
        setVenuePriceProcessing(true);
        try {
            const result = await bookingApi.applyVenueChangeAdjustment(booking.id, {
                ...payload,
                newLocationId: venueChangeData.newLocationId,
            });
            const adj = result.adjustment;
            if (adj?.type === "refund") {
                toast.success("Venue updated and refund issued. The student has been notified.");
            } else if (adj?.type === "charge") {
                if (adj.chatMessagePosted) {
                    toast.success("Venue updated. Note posted to the student's messages and a payment link emailed.");
                } else if (adj.emailSent) {
                    toast.success("Venue updated. Payment request emailed to the student.");
                } else {
                    toast.success("Venue updated. A payment link was generated for the student.");
                }
            } else {
                toast.success("Venue updated without a payment change.");
            }
            setShowVenuePriceModal(false);
            setVenueChangeData(null);
            setIsEditMode(false);
            // Refresh the dashboard so the venue/price change shows immediately, matching
            // the cancel/return flow (onReturnIssued === loadDashboardData). Without this the
            // change only appears after a manual Refresh.
            if (onReturnIssued) await onReturnIssued();
            onClose();
        } catch (err) {
            toast.error(err.message || "Failed to update the venue.");
        } finally {
            setVenuePriceProcessing(false);
        }
    };

    const handleVenuePriceModalClose = () => {
        setShowVenuePriceModal(false);
        setVenueChangeData(null);
    };

    // Confirm the nudge in the chosen mode ('gap' = up to next gap, 'whole' = rest of day).
    // The plan (which bookings + slots move, and where) is computed here and sent explicitly;
    // the backend just applies it. Location changes aren't carried on the nudge path.
    const handleConfirmNudge = async mode => {
        const { bookingShifts, slotShifts } = buildNudgePlan(mode);
        const nudgeData = {
            startTime: editedStartTime,
            endTime: editedEndTime,
            bookingShifts,
            slotShifts,
            nudgeFollowing: true, // Frontend routing flag (stripped by dashboard before API call)
        };

        await onUpdateTime(booking.id, nudgeData);

        setShowNudgeConfirm(false);
        setIsEditMode(false);
        onClose();
    };

    // Format date for display
    const formatDate = dateString => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-AU", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Calculate duration in hours
    const calculateDuration = (start, end) => {
        const durationMinutes = timeToMinutes(end) - timeToMinutes(start);
        const hours = durationMinutes / 60;
        if (hours === 1) return "1 hour";
        if (hours < 1) return `${durationMinutes} minutes`;
        return `${hours} hours`;
    };

    // Status badge styling
    const getStatusBadge = () => {
        // "pending" has two meanings (trust ladder): awaiting instructor approval
        // (pay-later) vs awaiting the student's payment (before-lesson) — the pill
        // must say which, or an awaiting-payment booking reads as needing approval.
        const isAwaitingStudentPayment =
            booking.status === "pending" &&
            booking.pricing?.paymentTiming === "before-lesson" &&
            booking.pricing?.paymentStatus !== "paid";
        const statusMap = {
            pending: {
                label: isAwaitingStudentPayment ? "Pending — Awaiting Student Payment" : "Pending Approval",
                color: "var(--dl-color-needs-action)",
                bg: "var(--dl-color-orange-50)",
            },
            confirmed: {
                label: "Confirmed",
                color: "var(--dl-color-turquoise-default)",
                bg: "color-mix(in srgb, var(--dl-color-turquoise-default) 20%, transparent)",
            },
            completed: { label: "Completed", color: "var(--dl-color-gray-white)", bg: "var(--dl-color-purple-900)" },
            paid: { label: "Paid", color: "var(--dl-color-purple-1000)", bg: "var(--dl-color-purple-50)" },
        };

        // Handle cancelled status with cancellationType differentiation
        if (booking.status === "cancelled") {
            const cancellationTypes = {
                rejection: { label: "Rejected", color: "var(--dl-color-sick-red)", bg: "var(--dl-color-danger-50)" },
                "instructor-cancel": {
                    label: "Cancelled by Instructor",
                    color: "var(--dl-color-needs-action)",
                    bg: "var(--dl-color-orange-50)",
                },
                "student-cancel": {
                    label: "Cancelled by Student",
                    color: "var(--dl-color-sick-red)",
                    bg: "var(--dl-color-danger-50)",
                },
                "instructor-sick-day": {
                    label: "Cancelled (Sick Day)",
                    color: "var(--dl-color-needs-action)",
                    bg: "var(--dl-color-orange-50)",
                },
            };

            const cancellationStatus = cancellationTypes[booking.cancellationType] || {
                label: "Cancelled",
                color: "var(--dl-color-text-muted)",
                bg: "var(--dl-color-gray-200)",
            };

            return (
                <span
                    className="status-badge"
                    style={{
                        backgroundColor: cancellationStatus.bg,
                        color: cancellationStatus.color,
                        border: `1px solid ${cancellationStatus.color}`,
                    }}>
                    {cancellationStatus.label}
                </span>
            );
        }

        const status = statusMap[booking.status] || statusMap.pending;
        return (
            <span
                className="status-badge"
                style={{
                    backgroundColor: status.bg,
                    color: status.color,
                    border: `1px solid ${status.color}`,
                }}>
                {status.label}
            </span>
        );
    };

    // Handle overlay click (close modal)
    const handleOverlayClick = e => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <>
            <div className="booking-modal-overlay" onClick={handleOverlayClick}>
                <div className="booking-modal-content">
                    {/* Header */}
                    <div className="booking-modal-header">
                        <div className="booking-modal-header-content">
                            <Icon
                                path={showReturnPanel ? mdiCreditCardRefund : mdiCalendarCheck}
                                size={1.2}
                                color="var(--dl-color-purple-default)"
                            />
                            <h2>
                                {showReturnPanel ? "Refund / Credit" : isEditMode ? "Edit Booking" : "Booking Details"}
                            </h2>
                        </div>
                        <button className="booking-modal-close" onClick={onClose}>
                            <Icon path={mdiClose} size={1} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="booking-modal-body">
                        {showReturnPanel ? (
                            <ReturnActionPanel
                                booking={booking}
                                onConfirm={handleReturnConfirm}
                                onSkip={() => setShowReturnPanel(false)}
                                isSubmitting={isIssuingReturn}
                                isGuest={isGuest}
                            />
                        ) : (
                            <>
                                {/* Student Info Section */}
                                <div className="booking-detail-section">
                                    <h3 className="booking-detail-section-title">Student Information</h3>

                                    {/* Guide text for manual bookings with unlinked users */}
                                    {booking.isManualEntry && booking.studentId === "000000000000000000000000" && (
                                        <p className="form-hint" style={{ marginBottom: "12px" }}>
                                            This booking was created manually for an unregistered App user, or from App
                                            server issues. You can link this to an appropriate registered user's account
                                            by clicking the button below.
                                        </p>
                                    )}

                                    <div className="student-info-row">
                                        <div className="student-avatar-large">
                                            <img
                                                src={booking.studentInfo?.profileImageUrl || avatarPlaceholder}
                                                alt={booking.studentName || "Student"}
                                                onError={e => {
                                                    e.target.onerror = null;
                                                    e.target.src = avatarPlaceholder;
                                                }}
                                            />
                                        </div>
                                        <div className="student-details">
                                            <div className="student-name-large">
                                                {booking.studentName || "Unknown Student"}
                                                {isGuest && <span className="booking-detail-guest-pill">Guest</span>}
                                            </div>
                                            {booking.studentInfo?.email && (
                                                <div className="student-contact">
                                                    <Icon path={mdiEmail} size={0.7} className="contact-icon" />
                                                    {booking.studentInfo.email}
                                                </div>
                                            )}
                                            {booking.studentInfo?.phone && (
                                                <div className="student-contact">
                                                    <Icon path={mdiPhone} size={0.7} className="contact-icon" />
                                                    {booking.studentInfo.phone}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* User Linking Section - Only show for manual bookings */}
                                    {booking.isManualEntry && booking.studentId === "000000000000000000000000" && (
                                        <div style={{ marginTop: "16px" }}>
                                            {!showLinkUser ? (
                                                <button
                                                    className="modal-btn modal-btn-primary"
                                                    onClick={() => setShowLinkUser(true)}
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}>
                                                    <Icon path={mdiAccountPlus} size={0.8} />
                                                    Link to User Account
                                                </button>
                                            ) : (
                                                <div className="link-user-interface">
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "8px",
                                                            marginBottom: "12px",
                                                        }}>
                                                        <Icon path={mdiAccountSearchOutline} size={0.9} />
                                                        <span style={{ fontWeight: 600, fontSize: "14px" }}>
                                                            Search for existing student
                                                        </span>
                                                    </div>

                                                    <input
                                                        type="text"
                                                        placeholder="Search by name or email..."
                                                        value={linkUserSearch}
                                                        onChange={e => {
                                                            setLinkUserSearch(e.target.value);
                                                            handleUserSearch(e.target.value);
                                                        }}
                                                        style={{
                                                            width: "100%",
                                                            padding: "10px 12px",
                                                            borderRadius: "8px",
                                                            border: "1px solid var(--dl-color-gray-300)",
                                                            fontSize: "14px",
                                                            marginBottom: "8px",
                                                        }}
                                                        disabled={isSearchingUsers || isLinking}
                                                    />

                                                    {isSearchingUsers && (
                                                        <div
                                                            style={{
                                                                padding: "12px",
                                                                textAlign: "center",
                                                                color: "var(--dl-color-text-muted)",
                                                            }}>
                                                            Searching...
                                                        </div>
                                                    )}

                                                    {linkUserResults.length > 0 && (
                                                        <div
                                                            className="user-search-results"
                                                            style={{
                                                                maxHeight: "200px",
                                                                overflowY: "auto",
                                                                border: "1px solid var(--dl-color-gray-300)",
                                                                borderRadius: "8px",
                                                                marginBottom: "12px",
                                                            }}>
                                                            {linkUserResults.map(user => (
                                                                <div
                                                                    key={user.id}
                                                                    onClick={() =>
                                                                        !isLinking && handleLinkUser(user.id)
                                                                    }
                                                                    style={{
                                                                        padding: "12px",
                                                                        borderBottom:
                                                                            "1px solid var(--dl-color-border)",
                                                                        cursor: isLinking ? "wait" : "pointer",
                                                                        transition: "background-color 0.2s",
                                                                        backgroundColor: "white",
                                                                    }}
                                                                    onMouseEnter={e =>
                                                                        (e.currentTarget.style.backgroundColor =
                                                                            "var(--dl-color-gray-50)")
                                                                    }
                                                                    onMouseLeave={e =>
                                                                        (e.currentTarget.style.backgroundColor =
                                                                            "white")
                                                                    }>
                                                                    <div style={{ fontWeight: 600, fontSize: "14px" }}>
                                                                        {user.username}
                                                                    </div>
                                                                    {user.email && (
                                                                        <div
                                                                            style={{
                                                                                fontSize: "13px",
                                                                                color: "var(--dl-color-text-muted)",
                                                                                marginTop: "2px",
                                                                            }}>
                                                                            {user.email}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {linkUserSearch.length >= 2 &&
                                                        !isSearchingUsers &&
                                                        linkUserResults.length === 0 && (
                                                            <div
                                                                style={{
                                                                    padding: "12px",
                                                                    textAlign: "center",
                                                                    color: "var(--dl-color-text-muted)",
                                                                    fontSize: "14px",
                                                                }}>
                                                                No students found
                                                            </div>
                                                        )}

                                                    <button
                                                        className="btn-secondary"
                                                        onClick={() => {
                                                            setShowLinkUser(false);
                                                            setLinkUserSearch("");
                                                            setLinkUserResults([]);
                                                        }}
                                                        style={{ marginTop: "8px" }}
                                                        disabled={isLinking}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Show linked user info if already linked */}
                                    {booking.isManualEntry && booking.studentId !== "000000000000000000000000" && (
                                        <div
                                            style={{
                                                marginTop: "16px",
                                                padding: "12px",
                                                backgroundColor: "var(--dl-color-green-50)",
                                                border: "1px solid var(--dl-color-green-border)",
                                                borderRadius: "8px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}>
                                            <Icon
                                                path={mdiAccountPlus}
                                                size={0.8}
                                                style={{ color: "var(--dl-color-green-500)" }}
                                            />
                                            <span style={{ fontSize: "14px", color: "var(--dl-color-green-default)" }}>
                                                <strong>Linked from manual booking to user account:</strong>{" "}
                                                {booking.studentName}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Booking Details Section */}
                                <div className="booking-detail-section">
                                    <h3 className="booking-detail-section-title">Lesson Details</h3>
                                    <div className="detail-row">
                                        <span className="detail-label">Status:</span>
                                        {getStatusBadge()}
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Date:</span>
                                        <span className="detail-value">{formatDate(booking.bookingDate)}</span>
                                    </div>

                                    {/* Time Edit Controls */}
                                    {isEditMode ? (
                                        <>
                                            {/* Quick Adjustment Buttons */}
                                            <div className="time-edit-section">
                                                <span className="detail-label">Quick Adjust:</span>
                                                <div className="quick-adjust-buttons">
                                                    <button onClick={() => handleQuickAdjust(-60)}>-1Hr</button>
                                                    <button onClick={() => handleQuickAdjust(-30)}>-30</button>
                                                    <button onClick={() => handleQuickAdjust(-15)}>-15</button>
                                                    <button onClick={() => handleQuickAdjust(-5)}>-5</button>
                                                    <button onClick={() => handleQuickAdjust(5)}>+5</button>
                                                    <button onClick={() => handleQuickAdjust(15)}>+15</button>
                                                    <button onClick={() => handleQuickAdjust(30)}>+30</button>
                                                    <button onClick={() => handleQuickAdjust(60)}>+1Hr</button>
                                                </div>
                                            </div>

                                            {/* Time Pickers — start + end on one row, Google-style dropdowns (matches Manage Availability) */}
                                            <div className="time-edit-section">
                                                <div className="booking-time-row">
                                                    <div className="booking-time-field">
                                                        <span className="detail-label">Start Time:</span>
                                                        <TimeSelectDropdown
                                                            value={editedStartTime}
                                                            onChange={v => handleTimeChange("start", v)}
                                                            ariaLabel="Start time"
                                                            zIndex={10001}
                                                        />
                                                    </div>
                                                    <div className="booking-time-field">
                                                        <span className="detail-label">End Time:</span>
                                                        <TimeSelectDropdown
                                                            value={editedEndTime}
                                                            onChange={v => handleTimeChange("end", v)}
                                                            ariaLabel="End time"
                                                            zIndex={10001}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Travel pill — position 2, directly under the time pickers so it
                    always sits above the location context in edit mode. */}
                                            {renderTravelBanner()}

                                            {/* Location Selector — filtered by whether the booking has a travel contextId */}
                                            {instructorLocations.length > 0 &&
                                                (() => {
                                                    const isTravelBooking = !!booking.location?.contextId;
                                                    const active = instructorLocations.filter(
                                                        l => l.isActive !== false
                                                    );
                                                    const filteredLocations = isTravelBooking
                                                        ? active.filter(l => l.isTravel === true)
                                                        : active.filter(l => l.isTravel !== true);
                                                    return (
                                                        <div className="time-edit-section">
                                                            <span className="detail-label">Location:</span>
                                                            <select
                                                                value={editedLocationId}
                                                                onChange={e => setEditedLocationId(e.target.value)}
                                                                className="time-picker-input">
                                                                <option value="">Location TBA</option>
                                                                {filteredLocations.map(location => (
                                                                    <option key={location.id} value={location.id}>
                                                                        {location.description}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    );
                                                })()}

                                            {/* Pricing Preview in Edit Mode */}
                                            {previewPricing && (
                                                <div className="pricing-preview-section">
                                                    <h4 className="pricing-preview-title">
                                                        Updated Pricing Preview
                                                        {previewPricing.isEstimated && (
                                                            <span className="pricing-estimated-badge">~ Estimated</span>
                                                        )}
                                                    </h4>
                                                    <div className="pricing-breakdown">
                                                        <div className="pricing-row">
                                                            <span className="pricing-label">Artist Fee:</span>
                                                            <span className="pricing-value">
                                                                {formatCurrencyFromMinor(
                                                                    previewPricing.artistFeeSubtotal,
                                                                    previewPricing.currency
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="pricing-row">
                                                            <span className="pricing-label">Venue Hire:</span>
                                                            <span className="pricing-value">
                                                                {formatCurrencyFromMinor(
                                                                    previewPricing.venueHireSubtotal,
                                                                    previewPricing.currency
                                                                )}
                                                            </span>
                                                        </div>
                                                        {previewPricing.venueHireOriginal && (
                                                            <div className="pricing-conversion-note">
                                                                (Converted from{" "}
                                                                {formatCurrencyFromMinor(
                                                                    previewPricing.venueHireOriginal.amount,
                                                                    previewPricing.venueHireOriginal.currency
                                                                )}
                                                                )
                                                            </div>
                                                        )}
                                                        <div className="pricing-row">
                                                            <span className="pricing-label">Platform Fee (2.5%):</span>
                                                            <span className="pricing-value">
                                                                {formatCurrencyFromMinor(
                                                                    previewPricing.platformFeeSubtotal,
                                                                    previewPricing.currency
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="pricing-row pricing-total">
                                                            <span className="pricing-label">Total Amount:</span>
                                                            <span className="pricing-value">
                                                                {formatCurrencyFromMinor(
                                                                    previewPricing.amount,
                                                                    previewPricing.currency
                                                                )}
                                                            </span>
                                                        </div>
                                                        {previewPricing.isEstimated && (
                                                            <div className="pricing-disclaimer">
                                                                Currency conversion is estimated. Final amount will be
                                                                calculated by Stripe.
                                                            </div>
                                                        )}
                                                        {/* Comparison with current pricing.
                          Paid bookings only refund/charge the venue-fee delta
                          (platform + processing fees are not refunded on the
                          original transaction). Show the venue delta only so
                          the indicator matches what the VenuePriceChangeModal
                          will actually offer. Unpaid bookings get a full
                          pricing recalc on save, so the total delta is honest. */}
                                                        {(() => {
                                                            const isPaid = booking.pricing?.paymentStatus === "paid";
                                                            const oldVenue = booking.pricing.venueHireSubtotal || 0;
                                                            const newVenue = previewPricing.venueHireSubtotal || 0;
                                                            const venueDelta = newVenue - oldVenue;
                                                            // The frontend preview omits processingFee from its total
                                                            // (calculatePricingPreview only sums artist + venue + platform),
                                                            // but booking.pricing.amount INCLUDES processingFee. Subtract
                                                            // processingFee from the baseline so the delta is honest —
                                                            // otherwise a same-fee venue swap surfaces a phantom drop
                                                            // equal to the processing fee.
                                                            const oldProcessingFee =
                                                                booking.pricing.processingFeeSubtotal || 0;
                                                            const oldTotalSansProcessing =
                                                                (booking.pricing.amount || 0) - oldProcessingFee;
                                                            const totalDelta =
                                                                previewPricing.amount - oldTotalSansProcessing;
                                                            const delta = isPaid ? venueDelta : totalDelta;
                                                            if (delta === 0) return null;
                                                            const isDown = delta < 0;
                                                            const label = isPaid
                                                                ? isDown
                                                                    ? "student refund amount"
                                                                    : "extra charge to student"
                                                                : "new booking total";
                                                            return (
                                                                <div className="pricing-change-indicator">
                                                                    <span
                                                                        style={{
                                                                            color:
                                                                                delta > 0
                                                                                    ? "var(--dl-color-needs-action)"
                                                                                    : "var(--dl-color-turquoise-default)",
                                                                            fontWeight: 600,
                                                                            fontSize: "14px",
                                                                        }}>
                                                                        {isDown ? "↓" : "↑"}{" "}
                                                                        {formatCurrencyFromMinor(
                                                                            Math.abs(delta),
                                                                            previewPricing.currency
                                                                        )}{" "}
                                                                        {label}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Overlap Warning */}
                                            {(overlaps.length > 0 || slotConflict) && (
                                                <div className="edit-overlap-warning">
                                                    <Icon path={mdiAlert} size={0.8} className="warning-icon" />
                                                    <div className="overlap-warning-content">
                                                        <p className="overlap-warning-title">
                                                            {overlaps.length > 0
                                                                ? `This time conflicts with ${overlaps.length} other booking${overlaps.length > 1 ? "s" : ""}:`
                                                                : "This time runs into your availability slots."}
                                                        </p>
                                                        {overlaps.length > 0 && (
                                                            <ul className="overlap-list">
                                                                {overlaps.map((b, idx) => (
                                                                    <li key={idx}>
                                                                        {b.studentName} ({b.timeSlot.startTime}-
                                                                        {b.timeSlot.endTime})
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        <p className="overlap-instruction">
                                                            Saving will ask whether to shift the following bookings and
                                                            slots up to the next break, or the rest of the day.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="detail-row">
                                                <span className="detail-label">Duration:</span>
                                                <span className="detail-value">
                                                    {calculateDuration(editedStartTime, editedEndTime)}
                                                </span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="detail-row">
                                                <span className="detail-label">Time:</span>
                                                <span className="detail-value">
                                                    {booking.timeSlot.startTime} - {booking.timeSlot.endTime}
                                                </span>
                                                {canEditDetails && (
                                                    <button
                                                        className="booking-shift-start-btn"
                                                        onClick={() => setIsEditMode(true)}>
                                                        <Icon path={mdiClockEditOutline} size={0.6} />
                                                        Shift Lesson Start
                                                    </button>
                                                )}
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">Duration:</span>
                                                <span className="detail-value">
                                                    {calculateDuration(
                                                        booking.timeSlot.startTime,
                                                        booking.timeSlot.endTime
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    )}

                                    {/* Travel pill — view mode only. In edit mode it renders at position 2
                (under the time pickers) above. */}
                                    {!isEditMode && renderTravelBanner()}

                                    {/* Read-only location row — hidden in edit mode because the
                editable location dropdown above already shows + lets the user
                change the venue. Showing both is redundant and confusing. */}
                                    {booking.location && !isEditMode && (
                                        <div className="detail-row">
                                            <span className="detail-label">Location:</span>
                                            <span className="detail-value">
                                                <Icon path={mdiMapMarker} size={0.7} className="location-icon" />
                                                {booking.location.description || "Not specified"}
                                                {booking.location.description?.includes("TBA") && (
                                                    <span
                                                        style={{
                                                            color: "var(--dl-color-orange-default)",
                                                            marginLeft: "8px",
                                                            fontStyle: "italic",
                                                        }}>
                                                        (Instructor will confirm venue)
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Pricing Section */}
                                {booking.pricing && !isEditMode && (
                                    <div className="booking-detail-section">
                                        <h3 className="booking-detail-section-title">Pricing Breakdown</h3>

                                        {/* Server-computed breakdown (effectivePricing) — rendered verbatim.
                  Rows appear only when the backend says they apply: credit line
                  when credit was applied to a pending/card-paid booking, service
                  fee only when a card payment incurred one. */}
                                        {ep ? (
                                            <>
                                                {ep.lessonFee > 0 && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Lesson fee:</span>
                                                        <span className="detail-value">
                                                            {formatCurrencyFromMinor(ep.lessonFee, ep.currency)}
                                                        </span>
                                                    </div>
                                                )}
                                                {ep.venueFee > 0 && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Venue fee:</span>
                                                        <span className="detail-value">
                                                            {booking.pricing.isEstimated && "~ "}
                                                            {formatCurrencyFromMinor(ep.venueFee, ep.currency)}
                                                        </span>
                                                    </div>
                                                )}
                                                {booking.pricing.venueHire?.original && (
                                                    <div
                                                        className="detail-row"
                                                        style={{
                                                            paddingLeft: "16px",
                                                            fontSize: "12px",
                                                            color: "var(--dl-color-gray-600)",
                                                            fontStyle: "italic",
                                                        }}>
                                                        <span>
                                                            (Converted from{" "}
                                                            {formatCurrencyFromMinor(
                                                                booking.pricing.venueHire.original.amount,
                                                                booking.pricing.venueHire.original.currency
                                                            )}
                                                            )
                                                        </span>
                                                    </div>
                                                )}
                                                {ep.creditApplied > 0 && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Credit applied:</span>
                                                        <span className="detail-value">
                                                            -{formatCurrencyFromMinor(ep.creditApplied, ep.currency)}
                                                        </span>
                                                    </div>
                                                )}
                                                {ep.serviceFee > 0 && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Service fee:</span>
                                                        <span className="detail-value">
                                                            {formatCurrencyFromMinor(ep.serviceFee, ep.currency)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div
                                                    className="detail-row"
                                                    style={{
                                                        borderTop: "2px solid var(--dl-color-border)",
                                                        paddingTop: "8px",
                                                        marginTop: "8px",
                                                    }}>
                                                    <span className="detail-label">Total Amount:</span>
                                                    <span className="detail-value pricing-amount">
                                                        {booking.pricing.isEstimated && "~ "}
                                                        {formatCurrencyFromMinor(ep.total, ep.currency)}
                                                    </span>
                                                </div>
                                                {paidByLabel && (
                                                    <div className="detail-row">
                                                        <span
                                                            style={{
                                                                fontSize: "12px",
                                                                color: "var(--dl-color-gray-600)",
                                                                fontStyle: "normal",
                                                            }}>
                                                            No service fee (paid by {paidByLabel}).
                                                        </span>
                                                    </div>
                                                )}
                                                {booking.pricing.isEstimated && (
                                                    <div className="detail-row" style={{ marginTop: "8px" }}>
                                                        <span
                                                            style={{
                                                                fontSize: "12px",
                                                                color: "var(--dl-color-status-amber)",
                                                                fontStyle: "italic",
                                                            }}>
                                                            Currency conversion is estimated. Final amount will be
                                                            calculated by Stripe.
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            /* Pre-effectivePricing fallback (stale cached object) — total only */
                                            <div className="detail-row">
                                                <span className="detail-label">Total Amount:</span>
                                                <span className="detail-value pricing-amount">
                                                    {formatCurrencyFromMinor(
                                                        booking.pricing.amount || booking.pricing.totalAmount,
                                                        booking.pricing.currency
                                                    )}
                                                </span>
                                            </div>
                                        )}

                                        {booking.pricing.paymentStatus && (
                                            <div className="detail-row">
                                                <span className="detail-label">Payment:</span>
                                                {booking.pricing.paymentStatus === "paid" ? (
                                                    // Settled state. External (cash/EFTPOS/bank transfer) reads "marked as
                                                    // paid" — the admin asserted it; Stripe reads "received". Both are green
                                                    // (the school has the money); the reconcile note + mini-card sync icon
                                                    // carry the external distinction, not colour.
                                                    <span className="booking-detail-paid-label">
                                                        {booking.pricing.paidOutsidePlatform
                                                            ? "Payment marked as paid"
                                                            : "Payment received"}
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="detail-value"
                                                        style={{ textTransform: "capitalize" }}>
                                                        {/* 'credited'/'refunded' mean the original card payment was RETURNED
                          (credit OUT of this booking) — distinct from a booking PAID BY
                          credit (paidBy 'platform_credit', credit INTO the booking). The
                          raw status word "credited" read ambiguously next to "paid by
                          account credit", so spell it out. */}
                                                        {booking.pricing.paymentStatus === "credited"
                                                            ? "Returned as credit"
                                                            : booking.pricing.paymentStatus === "refunded"
                                                              ? "Refunded"
                                                              : booking.pricing.paymentStatus}
                                                    </span>
                                                )}
                                                {booking.pricing.paymentStatus !== "paid" &&
                                                    booking.pricing.paymentStatus !== "refunded" &&
                                                    booking.pricing.paymentStatus !== "credited" &&
                                                    (booking.pricing.amount || 0) > 0 && (
                                                        <div
                                                            style={{
                                                                marginLeft: "auto",
                                                                display: "flex",
                                                                gap: "8px",
                                                                flexWrap: "wrap",
                                                            }}>
                                                            {/* Two distinct intents: Mark paid (record an already-received external
                          payment, straight to the method picker) vs Request Payment (send a
                          pay link through the app). */}
                                                            <button
                                                                className="base-modal-secondary-btn"
                                                                onClick={() => {
                                                                    setMarkPaidMode(true);
                                                                    setShowPaymentModal(true);
                                                                }}>
                                                                <Icon path={mdiCashCheck} size={1} />
                                                                Mark paid
                                                            </button>
                                                            <button
                                                                className="base-modal-secondary-btn"
                                                                onClick={() => {
                                                                    setMarkPaidMode(false);
                                                                    setShowPaymentModal(true);
                                                                }}>
                                                                <Icon path={mdiCreditCardClockOutline} size={1} />
                                                                Request Payment
                                                            </button>
                                                        </div>
                                                    )}
                                            </div>
                                        )}
                                        {/* Externally-paid reconcile note — plain inline, green sync icon, no tint.
                  Names the rail the admin chose (bank transfer / EFTPOS / cash / other).
                  Reconcile is an optional accounting axis, never "action required". */}
                                        {booking.pricing.paymentStatus === "paid" &&
                                            booking.pricing.paidOutsidePlatform &&
                                            (() => {
                                                const m = booking.pricing.paymentMethod;
                                                const methodLabel =
                                                    m === "eftpos"
                                                        ? "EFTPOS"
                                                        : m === "cash"
                                                          ? "cash"
                                                          : m === "bank_transfer"
                                                            ? "bank transfer"
                                                            : m === "other"
                                                              ? booking.pricing.externalPaymentNotes || "another method"
                                                              : null;
                                                return (
                                                    <div className="booking-detail-reconcile-note">
                                                        <Icon
                                                            path={mdiCreditCardSyncOutline}
                                                            size={0.9}
                                                            color="var(--dl-color-greentick)"
                                                        />
                                                        <p className="booking-detail-reconcile-note-text">
                                                            Paid externally{methodLabel ? ` by ${methodLabel}` : ""}.
                                                            <br />
                                                            Reconcile in Manage Finances whenever you balance your books
                                                            (optional).
                                                        </p>
                                                    </div>
                                                );
                                            })()}
                                        {booking.returnStatus && booking.returnStatus !== "none" && (
                                            <div className="detail-row">
                                                <span className="detail-label">
                                                    {booking.returnStatus === "credited" ? "Credit:" : "Refund:"}
                                                </span>
                                                <span
                                                    className="detail-value"
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "4px",
                                                        color: "var(--dl-color-turquoise-default)",
                                                        fontWeight: 600,
                                                    }}>
                                                    <Icon
                                                        path={mdiCreditCardRefund}
                                                        size={0.75}
                                                        color="var(--dl-color-turquoise-default)"
                                                    />
                                                    {booking.returnStatus === "partial"
                                                        ? "Partial refund issued"
                                                        : booking.returnStatus === "refunded"
                                                          ? "Refund issued"
                                                          : "Credit issued"}
                                                    {booking.returnAmount != null &&
                                                        ` (${formatCurrency(booking.returnAmount, ep?.currency || booking.pricing.currency)})`}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Entry Point 2: student-cancelled paid booking return prompt */}
                                {!isEditMode && isStudentCancelledWithNoReturn && !showReturnPanel && (
                                    <div className="booking-detail-return-prompt">
                                        <Icon path={mdiAlertCircle} size={0.9} color="var(--dl-color-needs-action)" />
                                        <span>Student cancelled this paid booking. Issue a return?</span>
                                        <button className="btn-turquoise" onClick={() => setShowReturnPanel(true)}>
                                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <Icon path={mdiCreditCardRefund} size={0.75} />
                                                Refund/Credit
                                            </span>
                                        </button>
                                    </div>
                                )}
                                {/* Messages Section */}
                                {!isEditMode && (
                                    <div className="booking-detail-section">
                                        <div className="booking-detail-section-title">
                                            Messages
                                            {booking.unreadCount > 0 && (
                                                <span className="unread-badge">{booking.unreadCount} unread</span>
                                            )}
                                            <div className="messages-controls">
                                                <button
                                                    className="refresh-messages-btn"
                                                    onClick={handleRefreshMessages}
                                                    disabled={isLoadingMessages}
                                                    title="Refresh messages">
                                                    <Icon
                                                        path={mdiRefresh}
                                                        size={0.8}
                                                        spin={isLoadingMessages ? 1 : 0}
                                                    />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="messages-container">
                                            {/* Messages Thread */}
                                            <div className="messages-thread">
                                                {isLoadingMessages ? (
                                                    <div className="messages-loading">Loading messages...</div>
                                                ) : messages.length > 0 ? (
                                                    <>
                                                        {messages.map(msg => {
                                                            const isOwnMessage = msg.senderId === currentUserId;
                                                            return (
                                                                <div
                                                                    key={msg.id}
                                                                    className={`message-bubble ${isOwnMessage ? "message-sent" : "message-received"}`}>
                                                                    {!isOwnMessage && (
                                                                        <div className="message-sender">
                                                                            {msg.senderName}
                                                                        </div>
                                                                    )}
                                                                    <div className="message-text">{msg.message}</div>
                                                                    <div className="message-time">
                                                                        {formatMessageTime(msg.sentAt)}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        <div ref={messagesEndRef} />
                                                    </>
                                                ) : (
                                                    <div className="messages-empty">
                                                        <Icon
                                                            path={mdiMessageText}
                                                            size={1.5}
                                                            style={{ color: "var(--dl-color-gray-400)" }}
                                                        />
                                                        <p>No messages yet</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Message Input */}
                                            <div className="message-input-container">
                                                {messageError && (
                                                    <div className="message-error">
                                                        <Icon path={mdiAlert} size={0.7} />
                                                        {messageError}
                                                    </div>
                                                )}
                                                <textarea
                                                    className="message-input"
                                                    placeholder="Type your message..."
                                                    value={newMessage}
                                                    onChange={e => {
                                                        setNewMessage(e.target.value);
                                                        if (messageError) setMessageError(null); // Clear error on new input
                                                    }}
                                                    onKeyDown={e => {
                                                        if (e.key === "Enter" && !e.shiftKey) {
                                                            e.preventDefault();
                                                            handleSendMessage();
                                                        }
                                                    }}
                                                    maxLength={MESSAGE_CHAR_LIMIT}
                                                    rows={2}
                                                />
                                                <div className="message-input-footer">
                                                    <span className="message-char-count">
                                                        {newMessage.length}/{MESSAGE_CHAR_LIMIT}
                                                    </span>
                                                    <button
                                                        className="message-send-btn"
                                                        onClick={handleSendMessage}
                                                        disabled={!newMessage.trim() || isSendingMessage}>
                                                        <Icon
                                                            path={isSendingMessage ? mdiRefresh : mdiSend}
                                                            size={0.8}
                                                        />
                                                        {isSendingMessage ? "Sending..." : "Send"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Nudge Confirmation Dialog — two-choice prompt via the shared ConfirmationModal */}
                    <ConfirmationModal
                        isOpen={showNudgeConfirm}
                        title="This time shift affects other bookings"
                        message={
                            <>
                                Shift the following bookings and availability slots?
                                {"\n\n"}
                                <strong>Up to next break:</strong> stops when a break (like lunch) absorbs the shifted
                                slot.
                                {"\n"}
                                <strong>Rest of day:</strong> moves all following slots after by the same amount,
                                keeping the break duration intact.
                            </>
                        }
                        confirmText="Up to next break"
                        onConfirm={() => handleConfirmNudge("gap")}
                        variant="primary"
                        secondaryText="Rest of day"
                        onSecondary={() => handleConfirmNudge("whole")}
                        secondaryVariant="purple"
                        cancelText="Cancel"
                        onCancel={() => setShowNudgeConfirm(false)}
                        maxWidth={440}
                        overlayZIndex={10001}
                    />

                    {/* Footer Actions */}
                    {!showReturnPanel && (
                        <div className="booking-modal-footer">
                            {isEditMode ? (
                                <>
                                    <button className="modal-btn modal-btn-success" onClick={handleSaveTime}>
                                        <Icon path={mdiContentSave} size={0.8} />
                                        Save Changes
                                    </button>
                                    <button
                                        className="modal-btn modal-btn-warning"
                                        onClick={() => {
                                            setIsEditMode(false);
                                            setEditedStartTime(booking.timeSlot.startTime);
                                            setEditedEndTime(booking.timeSlot.endTime);
                                            setEditedLocationId(booking.location?.id || "");
                                            setOverlaps([]);
                                            setSlotConflict(false);
                                        }}>
                                        Cancel
                                    </button>
                                    <button className="modal-btn modal-btn-secondary" onClick={onClose}>
                                        <Icon path={mdiClose} size={0.8} />
                                        Close
                                    </button>
                                </>
                            ) : (
                                <>
                                    {isPending && (
                                        <>
                                            <button
                                                className="modal-btn modal-btn-approve"
                                                onClick={() => {
                                                    onConfirm(booking.id);
                                                    onClose();
                                                }}>
                                                <Icon path={mdiCheck} size={0.8} />
                                                Approve
                                            </button>
                                            <button
                                                className="modal-btn modal-btn-reject"
                                                onClick={() => onReject(booking.id)}>
                                                <Icon path={mdiClose} size={0.8} />
                                                Reject
                                            </button>
                                        </>
                                    )}
                                    {canEditDetails && (
                                        <button
                                            className="modal-btn modal-btn-primary"
                                            onClick={() => setIsEditMode(true)}>
                                            <Icon path={mdiPencil} size={0.7} />
                                            Edit
                                        </button>
                                    )}
                                    {/* Backend only accepts cancels for pending/confirmed
                                        (400 otherwise). Pending is covered by Reject above, so
                                        offer Cancel on confirmed only. */}
                                    {onCancel && booking.status === "confirmed" && (
                                        <button
                                            className="modal-btn modal-btn-danger"
                                            onClick={() => onCancel(booking.id)}>
                                            <Icon path={mdiTrashCan} size={0.7} />
                                            Cancel
                                        </button>
                                    )}
                                    {/* Entry Point 3: manual Refund/Credit on any paid booking */}
                                    {isPaidWithNoReturn && (
                                        <button
                                            className="modal-btn modal-btn-purple"
                                            onClick={() => setShowReturnPanel(true)}>
                                            <Icon path={mdiCreditCardRefund} size={0.8} />
                                            Refund/Credit
                                        </button>
                                    )}
                                    <button className="modal-btn modal-btn-secondary" onClick={onClose}>
                                        <Icon path={mdiClose} size={0.8} />
                                        Close
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {showVenuePriceModal && venueChangeData && (
                <VenuePriceChangeModal
                    isOpen={showVenuePriceModal}
                    onClose={handleVenuePriceModalClose}
                    oldVenueName={venueChangeData.oldVenueName}
                    newVenueName={venueChangeData.newVenueName}
                    oldFee={venueChangeData.oldFee}
                    newFee={venueChangeData.newFee}
                    delta={venueChangeData.delta}
                    currency={venueChangeData.currency}
                    onConfirm={handleVenueAdjustment}
                    isProcessing={venuePriceProcessing}
                    isGuest={isGuest}
                    defaultDraft={venueChangeData.defaultDraft}
                />
            )}

            {showPaymentModal && (
                <BookingPaymentModal
                    isOpen={showPaymentModal}
                    startInMarkPaid={markPaidMode}
                    onClose={() => {
                        setShowPaymentModal(false);
                        setMarkPaidMode(false);
                    }}
                    onSuccess={async () => {
                        // Mark-paid mutates this booking. Close the modals FIRST (instant), then refresh
                        // the list in the background — awaiting the (slow) dashboard refetch before closing
                        // made the detail modal linger. The detail modal holds a stale snapshot, so closing
                        // it fully (like the cancel/refund flows) is what shows the new state on reopen.
                        setShowPaymentModal(false);
                        setMarkPaidMode(false);
                        onClose();
                        if (onReturnIssued) onReturnIssued();
                    }}
                    booking={booking}
                />
            )}
        </>
    );
};

export default BookingDetailModal;
